import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import StatCard from "../../components/admin/StatCard";
import toast from "react-hot-toast";
import { getDoctors, resetUserPassword, toggleDoctorStatus } from "../../api/axios";
import { doctorImageUrl } from "../../api/imageUrl";

function Avatar({ name, image }) {
  const src = doctorImageUrl(image);
  if (src) {
    return <img src={src} alt={name} className="w-10 h-10 rounded-full object-cover shrink-0 border border-gray-100" onError={(e) => { e.target.style.display = "none"; }} />;
  }
  return (
    <div className="w-10 h-10 rounded-full bg-[#00342b] text-white flex items-center justify-center font-bold shrink-0">
      {(name || "?").trim().charAt(0).toUpperCase()}
    </div>
  );
}

function getStatusBadge(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "verified") return { label: "Active", className: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (value === "inactive") return { label: "Deactivated", className: "bg-slate-100 text-slate-600 border-slate-200" };
  return { label: "Pending", className: "bg-amber-100 text-amber-700 border-amber-200" };
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      const res = await resetUserPassword(user.email, password);
      if (res?.success) {
        toast.success(`Password reset for ${user.name}`);
        onDone();
      } else {
        toast.error(res?.message || "Failed to reset password");
      }
    } catch (err) {
      toast.error(err.message || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-[#00342b]">Reset Password</h3>
        <p className="text-sm text-[#3f4945] mt-2">
          Set a new password for <span className="font-semibold">{user.name}</span> ({user.email}).
        </p>
        <form onSubmit={submit} className="space-y-3 mt-4">
          <input
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-[#bfc9c4] rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#00342b] outline-none"
            required
            autoFocus
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full border border-[#bfc9c4] rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#00342b] outline-none"
            required
          />
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-[#bfc9c4] text-[#3f4945]">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-[#00342b] text-white font-semibold disabled:opacity-60">
              {saving ? "Saving…" : "Reset Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MemberUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [resettingUser, setResettingUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDoctors();
      const all = Array.isArray(res?.data) ? res.data : [];
      setItems(all.filter((d) => d.account_type === "member"));
    } catch (err) {
      console.error("Failed to load users:", err);
      setItems([]);
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((u) => String(u.status).toLowerCase() === "verified").length;
    const pending = items.filter((u) => String(u.status).toLowerCase() === "pending").length;
    const deactivated = items.filter((u) => String(u.status).toLowerCase() === "inactive").length;
    return { total, active, pending, deactivated };
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((u) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || [u.name, u.doctor_id, u.email, u.phone, u.specialty].some((v) => String(v || "").toLowerCase().includes(q));
      const matchesStatus = !status || String(u.status || "").toLowerCase() === status;
      return matchesSearch && matchesStatus;
    });
  }, [items, search, status]);

  const handleToggleActive = async (user) => {
    const current = String(user.status || "pending").toLowerCase();
    const next = current === "inactive" ? "verified" : "inactive";
    try {
      const res = await toggleDoctorStatus(user.id, next);
      if (res?.success) {
        toast.success(next === "inactive" ? `${user.name} deactivated` : `${user.name} activated`);
        load();
      } else {
        toast.error(res?.message || "Failed to update user");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update user");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#00342b]">User Management</h2>
          <p className="text-sm text-[#3f4945] mt-1">Manage member accounts with portal login access</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Total Users" value={stats.total} subtitle="Accounts with login access" />
          <StatCard title="Active" value={stats.active} subtitle="Verified members" />
          <StatCard title="Pending" value={stats.pending} subtitle="Awaiting review" />
          <StatCard title="Deactivated" value={stats.deactivated} subtitle="Login access disabled" />
        </div>

        <div className="bg-white rounded-2xl border border-[#e1e3e4] p-4 grid gap-3 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / ID / email / phone..."
            className="border border-[#bfc9c4] rounded-xl px-4 py-3 md:col-span-3 text-sm focus:ring-2 focus:ring-[#00342b] outline-none"
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#bfc9c4] rounded-xl px-4 py-3 bg-white text-sm">
            <option value="">All Status</option>
            <option value="verified">✅ Active</option>
            <option value="pending">⏳ Pending</option>
            <option value="inactive">⛔ Deactivated</option>
          </select>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] p-10 text-center text-[#3f4945]">Loading users…</div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] overflow-hidden">
            <p className="text-sm text-[#707975] px-5 pt-4">
              Showing <span className="font-bold text-[#00342b]">{filtered.length}</span> of {items.length} users
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mt-2">
                <thead>
                  <tr className="text-left text-[#707975] text-xs uppercase tracking-wide border-b border-[#e1e3e4]">
                    <th className="px-5 py-3 font-semibold">User</th>
                    <th className="px-5 py-3 font-semibold">Contact</th>
                    <th className="px-5 py-3 font-semibold">Specialty</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Joined</th>
                    <th className="px-5 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const badge = getStatusBadge(u.status);
                    return (
                      <tr key={u.id} className="border-b border-[#f0f1f1] hover:bg-gray-50/60 transition">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={u.name} image={u.profile_image} />
                            <div className="min-w-0">
                              <div className="font-semibold text-[#191c1d]">{u.name || "Unnamed"}</div>
                              <div className="text-xs text-[#707975] font-mono">{u.doctor_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="text-[#1f2422]">{u.email}</div>
                          <div className="text-xs text-[#707975]">{u.phone}</div>
                        </td>
                        <td className="px-5 py-3 text-[#1f2422]">{u.specialty || "—"}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.className}`}>{badge.label}</span>
                        </td>
                        <td className="px-5 py-3 text-[#707975]">{formatDate(u.created_at)}</td>
                        <td className="px-5 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setResettingUser(u)} className="px-3 py-1.5 rounded-lg border border-[#bfc9c4] text-xs font-semibold text-[#3f4945] hover:bg-gray-50 transition">
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleToggleActive(u)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${String(u.status).toLowerCase() === "inactive" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-red-50 text-red-700 hover:bg-red-100"}`}
                            >
                              {String(u.status).toLowerCase() === "inactive" ? "Activate" : "Deactivate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filtered.length && (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-[#a0aba5]">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ResetPasswordModal user={resettingUser} onClose={() => setResettingUser(null)} onDone={() => setResettingUser(null)} />
    </AdminLayout>
  );
}
